import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Check, Plus, X, Bookmark } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENERGY_META = {
  chill: { label: 'Chill', accent: '#7DD3FC' },
  upbeat: { label: 'Upbeat', accent: '#39FF14' },
  groovy: { label: 'Groovy', accent: '#FFB300' },
  dance: { label: 'Dance', accent: '#FF007F' },
};

export const AiPlaybackDefaultsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [energies, setEnergies] = useState([]);
  const [genres, setGenres] = useState([]);
  const [genre, setGenre] = useState(null);
  const [energy, setEnergy] = useState(null);
  const [baseline, setBaseline] = useState('');
  const [mode, setMode] = useState('auto');
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [applyingId, setApplyingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [tax, s, p] = await Promise.all([
        axios.get(`${API}/ai-library/taxonomy`),
        axios.get(`${API}/settings/ai-mode`, { params: { local_hour: new Date().getHours() } }),
        axios.get(`${API}/settings/ai-mode/presets`),
      ]);
      setEnergies(tax.data.energies || []);
      setGenres(tax.data.genres || []);
      const g = s.data.baseline_genre || (tax.data.genres || [])[0] || null;
      const e = s.data.baseline_energy || (tax.data.energies || [])[0] || null;
      setGenre(g);
      setEnergy(e);
      setBaseline(`${g}|${e}`);
      setMode(s.data.mode || 'auto');
      setPresets(p.data || []);
    } catch {
      toast.error('Could not load playback defaults');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = `${genre}|${energy}` !== baseline;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/ai-mode`, { baseline_genre: genre, baseline_energy: energy });
      setBaseline(`${genre}|${energy}`);
      toast.success('Base vibe saved — new plays start here');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error('Give the vibe a name first'); return; }
    if (!genre || !energy) return;
    setSavingPreset(true);
    try {
      const r = await axios.post(`${API}/settings/ai-mode/presets`, { name, genre, energy, mode });
      setPresets(r.data || []);
      setPresetName('');
      toast.success(`Saved "${name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save preset');
    } finally {
      setSavingPreset(false);
    }
  };

  const applyPreset = async (p) => {
    setApplyingId(p.id);
    try {
      const r = await axios.post(`${API}/settings/ai-mode/presets/${p.id}/apply`);
      setGenre(r.data.baseline_genre);
      setEnergy(r.data.baseline_energy);
      setBaseline(`${r.data.baseline_genre}|${r.data.baseline_energy}`);
      setMode(r.data.mode || 'auto');
      toast.success(`Now playing "${p.name}" — ${p.genre.replace(/-/g, ' ')} · ${p.energy}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not apply preset');
    } finally {
      setApplyingId(null);
    }
  };

  const deletePreset = async (p) => {
    try {
      const r = await axios.delete(`${API}/settings/ai-mode/presets/${p.id}`);
      setPresets(r.data || []);
      toast.success(`Removed "${p.name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete preset');
    }
  };

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-6" data-testid="ai-playback-defaults-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-[#39FF14]" />
        <h3 className="text-white font-bold text-base">AI Playback Defaults</h3>
      </div>
      <p className="text-gray-500 text-[11px] mb-4">
        Set your venue's base genre &amp; energy. Every time you log in and hit play, music starts here — then Auto climbs the energy ladder through the night, while Manual holds this vibe until you change it in the booth.
      </p>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 text-gray-500 animate-spin" /></div>
      ) : (
        <>
          {/* Saved vibe presets — one tap to switch */}
          <div className="flex items-center gap-2 mb-2">
            <Bookmark className="w-3.5 h-3.5 text-[#FF007F]" />
            <label className="text-xs text-gray-500 uppercase tracking-wider">Saved vibes</label>
          </div>
          {presets.length === 0 ? (
            <p className="text-[11px] text-gray-600 mb-3" data-testid="no-presets-hint">
              No saved vibes yet. Pick a genre + energy below and save it as "Brunch", "Peak Night", etc.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="preset-list">
              {presets.map((p) => (
                <div
                  key={p.id}
                  className="group flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/5 transition-all"
                  data-testid={`preset-chip-${p.id}`}
                >
                  <button
                    type="button"
                    onClick={() => applyPreset(p)}
                    disabled={applyingId === p.id}
                    data-testid={`apply-preset-${p.id}`}
                    className="flex flex-col items-start text-left"
                  >
                    <span className="text-xs font-bold text-white leading-tight flex items-center gap-1">
                      {applyingId === p.id && <Loader2 className="w-3 h-3 animate-spin" />}
                      {p.name}
                    </span>
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide leading-tight">
                      {(p.genre || '').replace(/-/g, ' ')} · {p.energy} · {p.mode}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => deletePreset(p)}
                    data-testid={`delete-preset-${p.id}`}
                    className="opacity-40 group-hover:opacity-100 text-gray-400 hover:text-[#FF007F] transition-all"
                    aria-label={`Delete ${p.name}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Save current selection as a named preset */}
          <div className="flex gap-2 mb-5">
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') savePreset(); }}
              maxLength={40}
              placeholder="Name this vibe (e.g. Peak Night)"
              data-testid="preset-name-input"
              className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-full px-4 py-2 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF007F]/50"
            />
            <Button
              type="button"
              onClick={savePreset}
              disabled={savingPreset || !presetName.trim() || !genre || !energy}
              data-testid="save-preset-btn"
              className="h-9 px-4 rounded-full font-bold text-xs bg-[#FF007F] text-white hover:bg-[#d60069] shrink-0"
            >
              {savingPreset ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Save vibe</>}
            </Button>
          </div>

          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5">Base genre</label>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {genres.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGenre(g)}
                data-testid={`base-genre-${g}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${genre === g ? 'bg-[#39FF14] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
              >
                {g.replace(/-/g, ' ')}
              </button>
            ))}
          </div>

          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5">Base energy</label>
          <div className="flex flex-wrap gap-1.5 mb-5">
            {energies.map((e) => {
              const meta = ENERGY_META[e] || { label: e, accent: '#FF007F' };
              const active = energy === e;
              return (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEnergy(e)}
                  data-testid={`base-energy-${e}`}
                  style={active ? { backgroundColor: meta.accent, color: '#000' } : {}}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all ${active ? '' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>

          <Button
            onClick={save}
            disabled={!dirty || saving || !genre || !energy}
            data-testid="ai-playback-defaults-save"
            className={`h-10 px-6 rounded-full font-bold text-sm ${dirty ? 'bg-[#39FF14] text-black hover:bg-[#2fd60f]' : 'bg-white/10 text-gray-400'}`}
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : dirty ? 'Save base vibe' : <><Check className="w-4 h-4 mr-2" /> Saved</>}
          </Button>
        </>
      )}
    </div>
  );
};

export default AiPlaybackDefaultsCard;
